import React, { useMemo } from 'react'
import { TariffModel, TariffTableData } from '../models/tariffs-model'
import { Header, TableComplete } from '@e3dc-react/shell/Elements/Table'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { nullsFirst } from '@e3dc-react/shell/Libs/SortAlgorithms'
import { Box, Paper, Stack } from '@mui/material'
import { AddButton } from '@e3dc-react/shell/Elements/Buttons/Buttons'
import { TariffsTableMenuIcon } from './TariffsTableMenuIcon'
import { useTableStyles } from '../../shared/styles/table.styles'
import { TariffsStatusChip } from '../../shared/components/TariffsStatusChip'
import { TariffCategoryCell } from './TariffCategoryCell'
import { getCurrencyValue } from '../../shared/utils/currency-value'
import useModals from 'src/Hooks/useModals'
import { currencyCodeToSymbol, useHagerBridgePermissions } from '../../shared'
import { useTypedSelector } from 'src/State/RootReducer'
import { useDispatch } from 'react-redux'
import { updateTariffsSort } from 'src/State/E-Mobility/EMobilityStateAction'
import { format } from '@e3dc-react/shell/Libs/date'
import { getTariffTableData } from '../utils/get-tariff-table-data'
import { tariffStatusSortFn } from '../../shared/utils/sort-tariff-status'
import { getInitialTariffSort } from '../utils/get-initial-tariff-sort'

interface TariffsTableDesktopViewProps {
  tariffs: TariffModel[]
  installationId: number
  dynamicTariffActivated: boolean
}

/**
 *  Tariffs desktop view table component with tariff button and tariffs table.
 *  Component is visible on desktop screen/view.
 *  Each row has 3 dot menu icon for edit and delete tariff.
 *  It requires tariffs data array as TariffModel and installationId as string.
 */
export const TariffsTableDesktopView: React.FC<TariffsTableDesktopViewProps> = ({ tariffs, installationId, dynamicTariffActivated }) => {
  const { t } = useCustomTranslation()
  const { classes } = useTableStyles()

  const tariffSort = useTypedSelector(state => state.eMobility.tariffsTable.sort)
  const dispatch = useDispatch()

  const { openAddTariffDialog, openTariffDetailsDialog } = useModals()
  const {
    permissions: { tariffPermissions },
  } = useHagerBridgePermissions()
  const currencySymbol = tariffs.length > 0 ? currencyCodeToSymbol(tariffs[0].currencyCode) : null

  const onSelectTableRow = (row: TariffTableData): void => {
    const tariff = tariffs.find(tariff => tariff.tariffId === row.tariffId)

    if (!tariff) {
      return
    }

    openTariffDetailsDialog({ installationId, tariffId: tariff.tariffId })
  }

  const tariffTableData = getTariffTableData(tariffs)

  const initialTariffSort = getInitialTariffSort(tariffSort, dynamicTariffActivated)

  const header = useMemo<Header<TariffTableData>[]>(
    () => [
      {
        value: 'tariffName',
        label: t('emobility.tariffs.tariff-name'),
        sortFunction: nullsFirst((a: string, b: string) => a.localeCompare(b)),
      },
      {
        value: 'gridPrice',
        label: t('emobility.tariffs.price'),
        valueRender: (_, __, tariff: TariffTableData | undefined) => {
          if (!tariff?.gridPrice) {
            return '-'
          }

          return getCurrencyValue(tariff.gridPrice, tariff.currencyCode)
        },
        sortFunction: nullsFirst((a: number, b: number) => {
          return a - b
        }),
      },
      {
        value: 'tariffStatus',
        label: t('emobility.tariffs.status'),
        valueRender: (status: TariffTableData['tariffStatus']) => {
          return <TariffsStatusChip status={status} />
        },
        sortFunction: (a: TariffTableData['tariffStatus'], b: TariffTableData['tariffStatus']) => tariffStatusSortFn(a, b),
      },
      {
        value: 'startDate',
        label: t('emobility.tariffs.startDate'),
        sortFunction: nullsFirst((a: string, b: string) => a.localeCompare(b)),
        valueRender: (startDate: TariffTableData['startDate']) => {
          return startDate ? format(new Date(startDate), t('common.format.date-fns-date')) : '-'
        },
      },
      {
        value: 'endDate',
        label: t('emobility.tariffs.endDate'),
        sortFunction: nullsFirst((a: string, b: string) => a.localeCompare(b)),
        valueRender: (endDate: TariffTableData['endDate']) => {
          return endDate ? format(new Date(endDate), t('common.format.date-fns-date')) : '-'
        },
      },
      {
        value: 'lastUpdated',
        label: t('emobility.tariffs.changed'),
        sortFunction: nullsFirst((a: string, b: string) => a.localeCompare(b)),
        valueRender: (lastUpdated: TariffTableData['lastUpdated']) => {
          return lastUpdated ? format(new Date(lastUpdated), t('common.format.date-fns-date')) : ''
        },
      },
      {
        value: 'tariffCategory',
        label: t('emobility.tariffs.tariff-category'),
        valueRender: (category: TariffTableData['tariffCategory']) => {
          return <TariffCategoryCell category={category} />
        },
        sortFunction: nullsFirst((a: TariffTableData['tariffStatus'], b: TariffTableData['tariffStatus']) => a.localeCompare(b)),
      },
      {
        value: 'tariffId',
        label: '',
        valueRender: (_, __, tariff: TariffTableData | undefined) => {
          const menuOptionTariff = tariffs.find(item => item.tariffId === tariff?.tariffId)

          if (!menuOptionTariff) {
            return <></>
          }

          return tariffPermissions.editTariff || tariffPermissions.deleteTariff ? (
            <TariffsTableMenuIcon tariff={menuOptionTariff} installationId={installationId} dynamicTariffActivated={dynamicTariffActivated} />
          ) : undefined
        },
        isSortable: false,
      },
      { value: 'menuIcon', label: '', width: 60 },
    ],
    [t, tariffs, tariffPermissions.editTariff, tariffPermissions.deleteTariff, installationId, dynamicTariffActivated]
  )

  return (
    <>
      {tariffPermissions.addTariff && (
        <Stack direction={'row'} justifyContent={'flex-end'} alignItems={'center'} mb={2}>
          <Box id="e-mobility-add-new-tariff-button">
            <AddButton
              buttonProps={{ variant: 'contained', color: 'primary' }}
              onClick={() => openAddTariffDialog({ installationId, currencySymbol })}
              label={t('emobility.tariffs.add-tariff')}
            />
          </Box>
        </Stack>
      )}
      <Paper>
        <Box id="e-mobility-tariff-table">
          <TableComplete
            header={header}
            data={tariffTableData}
            bodyElevation={0}
            footerElevation={0}
            headerElevation={0}
            coloredColumn
            rowClassName={classes.row}
            className={classes.table}
            headerClassName={classes.tableHeader}
            onSelect={onSelectTableRow}
            initialSort={initialTariffSort}
            onSortChange={sort => dispatch(updateTariffsSort(sort))}
          />
        </Box>
      </Paper>
    </>
  )
}
